import { useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Sparkles, Info, AlertCircle, Camera, FileDown, Mountain, Droplets, Leaf, TrendingUp, Target, Scissors, AlertTriangle, CloudSun, Lightbulb, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import YieldViz3D from '@/components/three/YieldViz3D';
import { CONFIDENCE_TONE, BENCHMARK_TONE, formatEuro, formatQ } from './yieldConstants';

function FactorBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-secondary/40 py-3 px-1 text-center">
      <Icon size={14} className="mx-auto text-chartreuse mb-1"/>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="font-mono font-bold text-sm">{value}</div>
    </div>
  );
}

const Cell = ({ l, v, cls = '' }) => (
  <div className="rounded-lg bg-secondary/60 py-2 px-2"><div className="text-[10px] text-muted-foreground">{l}</div><div className={`font-medium ${cls}`}>{v}</div></div>
);

function PrecisionBox({ margin, tips, calibration }) {
  if (margin == null) return null;
  return (
    <div className="rounded-xl border border-chartreuse/30 bg-chartreuse/5 p-4" data-testid="yield-precision-box">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Margine di errore reale</div>
        <div className="font-display text-2xl text-chartreuse" data-testid="yield-error-margin">±{margin}%</div>
      </div>
      {calibration && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-chartreuse" data-testid="yield-calibration-note">
          <Check size={12}/> {calibration.nota}
        </div>
      )}
      {tips?.length > 0 ? (
        <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground" data-testid="yield-precision-tips">
          {tips.map((t, i) => <li key={i} className="flex gap-2"><Lightbulb size={13} className="text-chartreuse shrink-0 mt-0.5"/>{t}</li>)}
        </ul>
      ) : (
        <div className="mt-2 text-xs text-muted-foreground">Hai fornito tutti i dati utili: stima alla massima precisione possibile.</div>
      )}
    </div>
  );
}

function SeasonBox({ data, notes }) {
  if (!data) return null;
  return (
    <div data-testid="yield-season-box">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><CloudSun size={12}/> Meteo di quest'anno (ultimi 12 mesi)</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <Cell l="Pioggia" v={`${data.pioggia_12m_mm} mm`}/>
        <Cell l="Gelate primavera" v={data.gelate_primaverili}/>
        <Cell l="Giorni >35°C" v={data.giorni_caldo_35}/>
        <Cell l="Max giorni secchi" v={data.max_giorni_secchi}/>
      </div>
      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
        {(notes || []).map((n, i) => <li key={i} className="flex gap-2"><span className="text-chartreuse">·</span><span>{n}</span></li>)}
      </ul>
    </div>
  );
}

export default function YieldResultPro({ result }) {
  const tone = CONFIDENCE_TONE[result.confidenza] || CONFIDENCE_TONE.bassa;
  const benchTone = BENCHMARK_TONE[result.benchmark_regionale?.label] || CONFIDENCE_TONE.bassa;
  const v = result.vision_analysis;
  const terrain = result.topografia_dati || {};
  const scenarios = result.scenari || [];
  const warnings = result.avvisi || [];
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    track('yield_pdf_pro_download', { crop: result.coltura, confidence: result.confidenza });
    try {
      const resp = await api.post('/ai/yield-report-pdf-pro', { payload: result }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `agrinexus_yieldai_pro_${result.coltura || 'partita'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Report PDF PRO scaricato');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-2xl glass-vp overflow-hidden" data-testid="yield-result">
      <div className="p-6 md:p-8 bg-gradient-to-br from-chartreuse/10 via-transparent to-terracotta/10 border-b border-white/5">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-chartreuse">Yield AI PRO · Report</div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Produzione stimata</div>
            <div className="mt-1 font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter text-chartreuse" data-testid="yield-result-value">{formatQ(result.quintali_stimati)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{formatQ(result.quintali_min)} – {formatQ(result.quintali_max)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{result.quintali_per_ettaro} q/ha</div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valore lordo</div>
            <div className="mt-1 font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter text-terracotta" data-testid="yield-result-euro">{formatEuro(result.valore_euro_medio)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{formatEuro(result.valore_euro_min)} – {formatEuro(result.valore_euro_max)}</div>
            <div className="mt-1 text-xs text-muted-foreground">€ {result.prezzo_qle_medio}/q (ISMEA)</div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full border ${tone}`} data-testid="yield-result-confidence">
            Confidenza {result.confidenza} · {result.confidenza_pct}%
          </span>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full border ${benchTone}`} data-testid="yield-result-benchmark">
            <Target size={10} className="inline mr-1 mb-0.5"/> {result.benchmark_regionale?.label} ({result.benchmark_regionale?.delta_pct >= 0 ? '+' : ''}{result.benchmark_regionale?.delta_pct}%)
          </span>
          <span className="text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full border border-border text-muted-foreground">
            {result.coltura}{result.varieta ? ` · ${result.varieta}` : ''} · {result.regione || 'Italia'}
          </span>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="mx-6 mt-5 rounded-xl border border-terracotta/30 bg-terracotta/10 p-3 text-xs space-y-1" data-testid="yield-warnings">
          {warnings.map((w, i) => <div key={i} className="flex gap-2"><AlertTriangle size={13} className="text-terracotta shrink-0 mt-0.5"/>{w}</div>)}
        </div>
      )}

      <YieldViz3D min={result.quintali_min} mid={result.quintali_stimati} max={result.quintali_max}/>

      <div className="p-6 space-y-5 text-sm">
        <PrecisionBox margin={result.margine_errore_pct} tips={result.consigli_precisione} calibration={result.calibrazione}/>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Fattori di correzione</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <FactorBox icon={Droplets} label="Clima" value={`${result.moltiplicatore_climatico}×`}/>
            <FactorBox icon={Mountain} label="Topografia" value={`${result.moltiplicatore_topografico}×`}/>
            <FactorBox icon={Leaf} label="Agronomia" value={`${result.moltiplicatore_agronomico}×`}/>
            <FactorBox icon={Camera} label="Visivo AI" value={`${result.moltiplicatore_visivo}×`}/>
          </div>
        </div>

        {terrain.elevation_m !== undefined && terrain.elevation_m !== null && (
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Mountain size={12}/> Topografia (DEM 30m)</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Cell l="Altitudine" v={`${terrain.elevation_m} m`}/>
              <Cell l="Pendenza" v={`${terrain.slope_pct} %`}/>
              <Cell l="Esposizione" v={{ N: 'Nord', S: 'Sud', E: 'Est', O: 'Ovest', piano: 'Piano' }[terrain.aspect] || terrain.aspect}/>
            </div>
            <div className="mt-2 text-xs text-muted-foreground italic">{result.topografia_note}</div>
          </div>
        )}

        {result.clima_dati && (
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Droplets size={12}/> Clima storico</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Cell l="Temp. media" v={`${result.clima_dati.temp_mean_c}°C`}/>
              <Cell l="Pioggia/anno" v={`${result.clima_dati.annual_rainfall_mm} mm`}/>
              <Cell l="Giorni piovosi" v={result.clima_dati.rainy_days_per_year}/>
            </div>
          </div>
        )}

        <SeasonBox data={result.annata_dati} notes={result.annata_note}/>

        {result.agronomia_note?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Scissors size={12}/> Impatto agronomico</div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {result.agronomia_note.map((n, i) => <li key={i} className="flex gap-2"><span className="text-chartreuse">·</span><span>{n}</span></li>)}
            </ul>
          </div>
        )}

        {v && (
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Camera size={12}/> Analisi visiva AI</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {v.vigore_vegetativo && <Cell l="Vigore" v={v.vigore_vegetativo} cls="capitalize"/>}
              {v.carico_produttivo && <Cell l="Carico" v={v.carico_produttivo} cls="capitalize"/>}
              {v.uniformita && <Cell l="Uniformità" v={v.uniformita} cls="capitalize"/>}
              {v.fase_fenologica && <Cell l="Fase" v={v.fase_fenologica} cls="text-[11px]"/>}
            </div>
            {Array.isArray(v.criticita_rilevate) && v.criticita_rilevate.length > 0 && (
              <div className="mt-3 rounded-lg bg-terracotta/10 border border-terracotta/30 p-3 text-xs">
                <div className="font-bold text-terracotta flex items-center gap-1"><AlertCircle size={12}/> Criticità osservate</div>
                <ul className="mt-1.5 list-disc list-inside space-y-0.5">{v.criticita_rilevate.map((c, i) => <li key={i}>{String(c)}</li>)}</ul>
              </div>
            )}
            {v.note_agronomiche && <div className="mt-2 text-xs text-muted-foreground italic">"{v.note_agronomiche}"</div>}
          </div>
        )}

        {scenarios.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><TrendingUp size={12}/> Scenari di miglioramento</div>
            <div className="space-y-2">
              {scenarios.map((s, i) => (
                <div key={i} data-testid={`yield-scenario-${i}`} className="rounded-xl border border-chartreuse/20 bg-chartreuse/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{s.titolo}</div>
                      <div className="text-xs text-muted-foreground mt-1">{s.descrizione}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-lg text-terracotta">{s.extra_euro_anno >= 0 ? '+' : ''}{formatEuro(s.extra_euro_anno)}</div>
                      <div className="text-[10px] text-muted-foreground">/anno · {s.uplift_pct >= 0 ? '+' : ''}{s.uplift_pct}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.ancoraggio_storico && (
          <div className="rounded-lg bg-chartreuse/5 border border-chartreuse/30 p-3 text-xs">
            <div className="font-bold text-chartreuse flex items-center gap-1"><Info size={12}/> Dato storico applicato</div>
            <div className="mt-1 text-muted-foreground">{result.ancoraggio_storico}</div>
          </div>
        )}

        <div className="rounded-lg bg-secondary/40 p-3 text-xs text-muted-foreground flex gap-2">
          <Info size={14} className="shrink-0 mt-0.5"/>
          <div>{result.disclaimer}</div>
        </div>

        <Button onClick={downloadPdf} disabled={downloading} data-testid="yield-download-pdf" className="w-full h-12 rounded-full" variant="outline">
          {downloading ? <><Sparkles size={16} className="mr-2 animate-spin"/> Generazione PDF…</> : <><FileDown size={16} className="mr-2"/> Scarica Report PDF PRO (4 pagine)</>}
        </Button>
      </div>
    </div>
  );
}
